import { Button as MUIButton } from '@mui/material'
import { produce } from 'immer'
import {
  createContext,
  type ReactNode,
  useContext,
  useMemo,
  useRef,
  useState,
} from 'react'
import {
  Dialog as AriaDialog,
  DialogTrigger as AriaDialogTrigger,
  Label as AriaLabel,
  Popover as AriaPopover,
  Tag as AriaTag,
  TagGroup as AriaTagGroup,
  TagList as AriaTagList,
} from 'react-aria-components'
import { twJoin } from 'tailwind-merge'
import IcBaselineArrowDropDown from '~icons/ic/baseline-arrow-drop-down'
import Text from 'src/components/ui/Text'
import {
  useBaseModelIdsForMarkFilter,
  useLoraCategories,
} from 'src/stores/model'
import { cx } from 'src/utils/cx'
import {
  ALL_OPTION,
  BASE_MODEL_TYPE_OPTIONS,
  MODEL_SOURCE_OPTIONS,
  MODEL_USAGE_OPTIONS,
  TIME_RANGE_OPTIONS,
} from './constants'
import type { ModelFilterValue } from './ModelFilter.helper'
import { modelFilterNormalizerHandler } from './ModelFilter.helper'

type ModelFilterProps = {
  value?: ModelFilterValue
  onChange?: (value: ModelFilterValue) => void
  disabled?:
    | boolean
    | Partial<Record<keyof ModelFilterValue, boolean | string[]>>
  hideDisabled?: boolean
}

export default function ModelFilter(props: StyleProps & ModelFilterProps) {
  function updateValue<K extends keyof ModelFilterValue>(
    key: K,
    val: ModelFilterValue[K],
  ) {
    props.onChange?.(
      produce(props.value ?? {}, value => {
        const proxy = new Proxy(value, modelFilterNormalizerHandler)
        if (val != null) {
          proxy[key] = val
        } else {
          delete proxy[key]
        }
      }),
    )
  }

  return (
    <ModelFilterContext.Provider value={{ ...props, updateValue }}>
      <ModelFilterActual {...props} />
    </ModelFilterContext.Provider>
  )
}

const ModelFilterContext = createContext(
  {} as ModelFilterProps & {
    updateValue: <K extends keyof ModelFilterValue>(
      key: K,
      val: ModelFilterValue[K],
    ) => void
  },
)

function ModelFilterActual(props: StyleProps & ModelFilterProps) {
  const { updateValue } = useContext(ModelFilterContext)
  const enabledFilters = useMemo(() => {
    return Object.entries(props.value ?? {}).filter(([k, v]) => {
      // Exclude disabled
      if (
        typeof props.disabled !== 'boolean' &&
        props.disabled?.[k as keyof ModelFilterValue]
      )
        return false
      // Exclude [], [""]
      if (Array.isArray(v) && (v.length === 0 || (v.length === 1 && !v[0])))
        return false
      return Boolean(v)
    }).length
  }, [props.value, props.disabled])

  const [open, setOpen] = useState(false)
  const triggerRef = useRef<HTMLButtonElement>(null)

  const isModelTypeSelected = props.value?.type === 'model'
  const isLoraTypeSelected = props.value?.type === 'lora'

  return (
    <AriaDialogTrigger isOpen={open} onOpenChange={setOpen}>
      {/* Ideally, it's better to use <UIButton> here.
          However, I'm tired to mimic the button style to match the order select
          beside this.
          So I'm leaving a note for now.
          TODO: Migrate to UIButton and match the styles of all buttons here */}
      <MUIButton
        ref={triggerRef}
        className={cx('px-3 bg-background-light font-normal', props.className)}
        style={props.style}
        onClick={() => setOpen(true)}
      >
        <span className="capitalize">
          <Text
            i18nKey="artwork:recipes.categories.filters"
            defaults="Filters"
          />
          {enabledFilters > 0 && (
            <span className="ml-1 px-1.5 py-px text-xs text-white dark:text-black bg-black dark:bg-white rounded-full">
              {enabledFilters}
            </span>
          )}
        </span>
        <IcBaselineArrowDropDown
          className={twJoin(
            '-mr-2 size-6 text-black/50 dark:text-white',
            open && 'rotate-180',
          )}
        />
      </MUIButton>
      <AriaPopover
        triggerRef={triggerRef}
        placement="bottom start"
        offset={4}
        className={twJoin(
          'flex flex-col',
          'entering:animate-in entering:fade-in entering:slide-in-from-top-1',
          'exiting:animate-out exiting:fade-out exiting:slide-out-to-top-1',
        )}
      >
        <AriaDialog
          className="flex-1 overflow-auto bg-white dark:bg-zinc-800 rounded-lg ring-1 ring-black/15 dark:ring-white/10 shadow-lg shadow-black/40 dark:shadow-black/60 divide-y divide-black/10 dark:divide-white/10"
          style={{ maxWidth: 320 }}
        >
          <ModelFilterSection
            value={props.value?.type ?? ''}
            onChange={type => updateValue('type', type)}
            label={
              <Text i18nKey="market:search.model-filters.options.model-usage.label" />
            }
            options={MODEL_USAGE_OPTIONS}
            disabled={resolveDisabled(props.disabled, 'type')}
          />
          <ModelFilterSection
            selectionMode="multiple"
            value={props.value?.modelTypes ?? []}
            onChange={value => {
              return updateValue('modelTypes', value)
            }}
            label={<Text i18nKey="market:submit.model-type" />}
            options={BASE_MODEL_TYPE_OPTIONS}
            disabled={
              !isModelTypeSelected ||
              resolveDisabled(props.disabled, 'modelTypes')
            }
          />
          <ModelFilterSection
            selectionMode="multiple"
            value={props.value?.baseModelTypes ?? []}
            onChange={value => {
              return updateValue('baseModelTypes', value)
            }}
            label={
              <Text i18nKey="market:search.model-filters.options.base-model.label" />
            }
            options={BASE_MODEL_TYPE_OPTIONS}
            disabled={
              !isLoraTypeSelected ||
              resolveDisabled(props.disabled, 'baseModelTypes')
            }
          />
          <BaseModelSection />
          <ModelFilterSection
            value={props.value?.source ?? ''}
            onChange={source => updateValue('source', source)}
            label={<Text i18nKey="common:source.label" />}
            options={MODEL_SOURCE_OPTIONS}
            disabled={
              !isLoraTypeSelected || resolveDisabled(props.disabled, 'source')
            }
          />
          <LoraCategorySection />
          <ModelFilterSection
            value={props.value?.time ?? ''}
            onChange={time => updateValue('time', time)}
            label={<Text i18nKey="market:model.posted-at" />}
            options={TIME_RANGE_OPTIONS}
            disabled={resolveDisabled(props.disabled, 'time')}
          />
        </AriaDialog>
      </AriaPopover>
    </AriaDialogTrigger>
  )
}

function BaseModelSection() {
  const { value, updateValue, disabled } = useContext(ModelFilterContext)
  const baseModelIds = useBaseModelIdsForMarkFilter()

  const isLoraTypeSelected = value?.type === 'lora'
  const isDisabled =
    !isLoraTypeSelected ||
    (['mmdit26a', 'dit7'] as const).some(t =>
      value?.baseModelTypes?.includes(t),
    ) ||
    resolveDisabled(disabled, 'baseModelId')

  return (
    <ModelFilterSection
      selectionMode="single"
      value={value?.baseModelId ?? ''}
      onChange={baseModelId => updateValue('baseModelId', baseModelId)}
      label={
        <Text i18nKey="market:search.model-filters.options.lora-base-model.label" />
      }
      options={[ALL_OPTION, ...baseModelIds]}
      disabled={isDisabled}
    />
  )
}

function LoraCategorySection() {
  const { value, updateValue, disabled } = useContext(ModelFilterContext)
  const loraCategories = useLoraCategories()

  const isLoraTypeSelected = value?.type === 'lora'

  return (
    <ModelFilterSection
      value={value?.category ?? ''}
      onChange={category => updateValue('category', category)}
      label={<Text i18nKey="common:category.label_lora" />}
      options={[
        { value: '', label: 'common:visibility.all' },
        ...loraCategories,
      ]}
      disabled={!isLoraTypeSelected || resolveDisabled(disabled, 'category')}
    />
  )
}

function resolveDisabled(
  value: ModelFilterProps['disabled'],
  key: keyof ModelFilterValue,
) {
  return typeof value === 'boolean' || value == null ? value : value[key]
}

const tagClassName = () =>
  twJoin(
    'min-w-12 px-2 py-1 text-center rounded-md ring-1 ring-inset cursor-default',
    'ring-black/15',
    'group-selected:text-white group-selected:bg-black',
    'dark:ring-white/15',
    'dark:group-selected:text-black dark:group-selected:bg-white',
    'group-disabled:opacity-60',
  )

function normalizeValueForSelectionSet<T>(value: T | T[]) {
  if (Array.isArray(value)) {
    if (value.length === 0) return new Set([''])
    return new Set(value)
  }
  return new Set([value || '']) // '' means 'all'
}

/**
 * A section (tag group) of the model filter
 */
function ModelFilterSection<T extends string | string[]>(props: {
  onChange?: (value: T) => void
  label: ReactNode
  options?: Array<{ value: string; label: string }>
  disabled?: boolean | string[]
  value: T
  selectionMode?: 'single' | 'multiple'
}) {
  const isMultiple = props.selectionMode === 'multiple'
  const { hideDisabled } = useContext(ModelFilterContext)
  const selection = normalizeValueForSelectionSet(props.value)

  if (!props.options?.length || (hideDisabled && props.disabled === true))
    return null

  return (
    <AriaTagGroup
      selectionMode={props.selectionMode ?? 'single'}
      disallowEmptySelection
      selectedKeys={selection}
      onSelectionChange={sel => {
        if (sel === 'all') return

        if (isMultiple) {
          if (sel.difference(selection).has('')) sel = new Set('') // If selected 'all': Clear other selections, keep 'all' only
          if (selection.has('') && selection.isSubsetOf(sel)) sel.delete('') // If other than 'all' is selected, unselect 'all'
        }

        const changedVal = (
          isMultiple ? [...sel] : ([...sel][0] as string)
        ) as T
        props.onChange?.(changedVal)
      }}
      disabledKeys={
        props.disabled === true
          ? props.options?.map(it => it.value)
          : props.disabled || undefined
      }
      className="p-3 text-sm"
    >
      <AriaLabel className="-my-1">{props.label}</AriaLabel>
      <AriaTagList items={props.options} className="mt-2 flex flex-wrap gap-2">
        {item => (
          <AriaTag id={item.value} className="group" textValue={item.label}>
            {({ isDisabled }) =>
              hideDisabled && isDisabled ? null : (
                <div className={tagClassName()}>
                  <Text i18nKey={item.label} defaults={item.label} />
                </div>
              )
            }
          </AriaTag>
        )}
      </AriaTagList>
    </AriaTagGroup>
  )
}
